import React from 'react';
import { Box, IconButton } from '@mui/material';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import type { ScenarioContextDto, ScenarioContextLocationDto } from '@/api-gen/api.schemas';
import type { ScenarioSettingsType } from 'app/components/ScenarioEditorPage/ScenarioSettings/hooks/useScenarioForm.ts';
import { createFormField } from '@/app/utils/formUtils.ts';
import VariableSuggestionInput from '../Variables/VariableSuggestionInput.tsx';
import SectionLabel from 'app/components/ScenarioEditorPage/components/SectionLabel.tsx';
import EmptyStateCard from 'app/components/ScenarioEditorPage/components/EmptyStateCard.tsx';

import { Close as CloseIcon, PlaceOutlined as PlaceIcon } from '@mui/icons-material';

import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/i18n/types/i18nNamespace.ts';
import type { I18nScenarioEditorNs } from '@/i18n/dictionaries/interfaces.ts';
import DashedAddButton from 'app/components/form/DashedAddButton.tsx';

const ScenarioContextLocationsCard: React.FunctionComponent = () => {
  const settingsField = createFormField<ScenarioSettingsType>();
  const contextField = createFormField<ScenarioContextDto>();
  const locationField = createFormField<ScenarioContextLocationDto>();

  const [translate] = useTranslation([I18nNamespace.ScenarioEditor]);

  const name = `${settingsField.context}.${contextField.locations}` as const;

  const { control, trigger } = useFormContext<ScenarioSettingsType>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: name,
    keyName: 'key'
  });

  const defaultLocation: ScenarioContextLocationDto = {
    location: translate(nameof.full<I18nScenarioEditorNs>((n) => n.sceneContextLocationsCard.newLocation)),
    id: 0
  };

  return (
    <Box>
      <SectionLabel>
        {translate(nameof.full<I18nScenarioEditorNs>((n) => n.sceneContextLocationsCard.locations))}
      </SectionLabel>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {!fields.length && (
          <EmptyStateCard
            compact
            icon={<PlaceIcon />}
            title={translate(nameof.full<I18nScenarioEditorNs>((n) => n.sceneContextLocationsCard.noLocationsAddedYet))}
            description={translate(nameof.full<I18nScenarioEditorNs>((n) => n.sceneContextLocationsCard.addLocationsDescription))}
            buttonLabel={translate(nameof.full<I18nScenarioEditorNs>((n) => n.sceneContextLocationsCard.addFirstLocation))}
            onAction={() => append(defaultLocation)}
          />
        )}

        {fields.map((field, fieldIndex) => {
          const prefix = `${name}.${fieldIndex}` as const;
          const fullName = `${prefix}.${locationField.location}` as const;

          return (
            <Box
              key={field.key}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.25,
                p: '10px 12px',
                backgroundColor: 'grey.50',
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: '6px',
                fontSize: 13,
                lineHeight: 1.55,
                transition: 'border-color 0.15s',
                '&:hover': { borderColor: 'grey.300' },
                '&:hover .delete-btn': { color: 'error.main' }
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Controller
                  control={control}
                  name={fullName}
                  defaultValue={field.location}
                  render={({ field: formField }) => (
                    <VariableSuggestionInput
                      inputProps={{
                        margin: 'none',
                        variant: 'standard',
                        InputProps: {
                          disableUnderline: true,
                          sx: { fontSize: 13, lineHeight: 1.55 },
                          inputProps: {
                            maxLength: 100,
                            maxRows: 5
                          }
                        }
                      }}
                      value={formField.value}
                      onChange={(value) => formField.onChange(value)}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name={`${prefix}.${locationField.id}` as const}
                  defaultValue={field.id}
                  render={() => <input type="hidden" />}
                />
              </Box>
              <IconButton
                className="delete-btn"
                onClick={() => {
                  remove(fieldIndex);
                  trigger(name);
                }}
                size="small"
                sx={{
                  color: 'grey.300',
                  transition: 'color 0.15s',
                  mt: '1px',
                  p: 0.5
                }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          );
        })}

          {fields.length > 0 && (
        <DashedAddButton onClick={() => append(defaultLocation)} icon={null}>
          {translate(nameof.full<I18nScenarioEditorNs>((n) => n.sceneContextLocationsCard.addLocation))}
        </DashedAddButton>
          )}
      </Box>
    </Box>
  );
};

export default ScenarioContextLocationsCard;
